import { useSelector } from 'react-redux'
import moment from 'moment'
import { Connections, getActivity, getBody, getDaily, getSleep } from 'terra-react'
import { useCallback } from 'react'
import { useTerraHealthConnect } from '@src/screens/Settings/utils/hooks'
import { terraProvidersSelector } from '@src/selectors/integrations'
import { Analytics, Bugsnag, CustomEventTypes, Device } from '@src/config'
import { Storage } from '@src/utils'
import { userSelector } from '@src/selectors/app'
import { TerraProviderKind } from '@src/types'

let isHealthConnectSyncing = false

const MAX_BACKFILL_DAYS = 7
const MANUAL_SYNC_INTERVAL_MINUTES = 2
const AUTOMATIC_SYNC_INTERVAL_MINUTES = 60

export const useSyncHealthConnectData = () => {
  const initTerraHealthConnect = useTerraHealthConnect()
  const providers = useSelector(terraProvidersSelector)
  const user = useSelector(userSelector)

  const provider = providers.find(
    (provider) => provider.providerKind === TerraProviderKind.HealthConnect,
  )

  const dataSyncTask = useCallback(
    async (startTimestamp: Date) => {
      const endTimestamp = new Date()

      try {
        const success = await initTerraHealthConnect()
        if (!success) {
          return
        }

        let processingSuccessful = true
        let dayBeingProcessedEndTimestamp = endTimestamp

        // Either start from the last sync date or max backfill timestamp
        let syncStartTimestamp = startTimestamp
        const maxBackfillTimestamp = moment(endTimestamp).subtract(MAX_BACKFILL_DAYS, 'days')
        if (moment(syncStartTimestamp).isBefore(maxBackfillTimestamp)) {
          syncStartTimestamp = maxBackfillTimestamp.toDate()
        }

        while (moment(dayBeingProcessedEndTimestamp).isAfter(syncStartTimestamp)) {
          const dayBeingProcessedStartTimestamp = moment(dayBeingProcessedEndTimestamp)
            .startOf('day')
            .toDate()

          const queryConfigs: {
            fn: (...args: any[]) => Promise<{ success: boolean }>
            args: [Connections, Date, Date, ...any[]]
          }[] = [
            {
              fn: getDaily,
              args: [
                Connections.HEALTH_CONNECT,
                dayBeingProcessedStartTimestamp,
                dayBeingProcessedEndTimestamp,
                true, // toWebhook
              ],
            },
            {
              fn: getBody,
              args: [
                Connections.HEALTH_CONNECT,
                dayBeingProcessedStartTimestamp,
                dayBeingProcessedEndTimestamp,
                false, // latestReading
                true, // toWebhook
              ],
            },
            {
              fn: getActivity,
              args: [
                Connections.HEALTH_CONNECT,
                dayBeingProcessedStartTimestamp,
                dayBeingProcessedEndTimestamp,
                true, // toWebhook
              ],
            },
            {
              fn: getSleep,
              args: [
                Connections.HEALTH_CONNECT,
                dayBeingProcessedStartTimestamp,
                dayBeingProcessedEndTimestamp,
                true, // toWebhook
              ],
            },
          ]

          // Run each query and immediately discard the result to free memory
          for (const config of queryConfigs) {
            const { fn, args } = config
            const { success } = await fn(...args)

            if (!success) {
              processingSuccessful = false
            }
          }

          dayBeingProcessedEndTimestamp = moment(dayBeingProcessedEndTimestamp)
            .subtract(1, 'day')
            .endOf('day')
            .toDate()
        }

        if (processingSuccessful) {
          Storage.set(
            `${Storage.HEALTH_CONNECT_LATEST_SYNC_TIMESTAMP_KEY}_${user?.id}`,
            endTimestamp,
          )
        }
      } catch (error: any) {
        Bugsnag.notify(error)
      }
    },
    [initTerraHealthConnect, user?.id],
  )

  const performSync = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!Device.android || !provider?.active) {
        return
      }

      if (isHealthConnectSyncing) {
        return
      }

      const latestAttemptedSyncTimestamp = Storage.get(
        `${Storage.HEALTH_CONNECT_LATEST_ATTEMPTED_SYNC_TIMESTAMP_KEY}_${user?.id}`,
      )

      const latestSyncTimestamp = Storage.get<string>(
        `${Storage.HEALTH_CONNECT_LATEST_SYNC_TIMESTAMP_KEY}_${user?.id}`,
      )

      if (latestAttemptedSyncTimestamp) {
        const minutesSinceLastAttempt = moment().diff(latestAttemptedSyncTimestamp, 'minutes')
        const intervalMinutes = force
          ? MANUAL_SYNC_INTERVAL_MINUTES
          : AUTOMATIC_SYNC_INTERVAL_MINUTES

        if (minutesSinceLastAttempt < intervalMinutes) {
          return
        }
      }

      isHealthConnectSyncing = true
      try {
        // Throttle retries even when initialization or a subsequent query fails.
        Storage.set(
          `${Storage.HEALTH_CONNECT_LATEST_ATTEMPTED_SYNC_TIMESTAMP_KEY}_${user?.id}`,
          new Date(),
        )
        const startTimestamp = latestSyncTimestamp
          ? new Date(latestSyncTimestamp)
          : moment(0).toDate()

        Analytics.track(CustomEventTypes.TerraHealthConnectSyncStarted, {
          startTimestamp,
        })

        await dataSyncTask(startTimestamp)
      } catch (error) {
        Bugsnag.notify(error as string)
      } finally {
        isHealthConnectSyncing = false
      }
    },
    [dataSyncTask, provider?.active, user?.id],
  )

  return performSync
}
